/**
 * AI Observability — the non-component half of the shared kit.
 *
 * Constants, formatters and the fetch hook live apart from `obs-shell.tsx` so
 * that file exports components and nothing else. That is not a style
 * preference: React Fast Refresh can only hot-reload a module whose exports are
 * all components, and mixing a constant in silently costs every page in the
 * section its live reload.
 */
import { useCallback, useEffect, useState } from 'react';


export const OBS_TABS = [
  { to: '/ai-observability', label: 'Overview', end: true },
  { to: '/ai-observability/tracing', label: 'Calls', end: false },
];

/** Stroked glyph paths, drawn by `<Icon>`. */
export const ICONS = {
  cost: 'M12 1v22M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6',
  calls: 'M3 12h4l2 6 4-14 2 8h6',
  tokens: 'M4 7h16M4 12h16M4 17h10',
  latency: 'M12 7v5l3 2M12 3a9 9 0 1 0 9 9',
  ttft: 'M13 2 4 14h7l-1 8 9-12h-7l1-8Z',
  layers: 'M12 3 2 8l10 5 10-5-10-5ZM2 16l10 5 10-5M2 12l10 5 10-5',
  model: 'M4 5h16v14H4zM9 5v14M15 5v14',
  table: 'M4 6h16M4 12h16M4 18h16',
  eval: 'M9 12l2 2 4-4M12 3a9 9 0 1 0 9 9',
  book: 'M4 19.5A2.5 2.5 0 0 1 6.5 17H20M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2Z',
  back: 'M15 18l-6-6 6-6',
  gear: 'M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6ZM19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1.1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.5-1.1 1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8V9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1Z',
};

/** The one red used for a failed run, so every surface marks failure alike. */
export const CHIP_FAIL = {
  background: '#FEE2E2',
  borderColor: '#FECACA',
  color: '#991B1B',
} as const;

/**
 * Timestamp styles, coarsest first. Named by what they show rather than by the
 * page that wanted them, so a new page picks a style instead of declaring a
 * sixth `Intl.DateTimeFormat` that is subtly unlike the other five.
 */
export const WHEN = {
  clock: { hour: '2-digit', minute: '2-digit', second: '2-digit' },
  minute: { month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit' },
  second: { month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' },
  full: {
    year: 'numeric', month: 'short', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  },
} as const satisfies Record<string, Intl.DateTimeFormatOptions>;

export type WhenStyle = keyof typeof WHEN;

const FORMATTERS = Object.fromEntries(
  Object.entries(WHEN).map(([k, opts]) => [k, new Intl.DateTimeFormat(undefined, opts)]),
) as Record<WhenStyle, Intl.DateTimeFormat>;

/**
 * Format a stored timestamp, or a dash.
 *
 * The dash matters more than the formatting. `Intl` throws a RangeError on an
 * invalid Date, and a throw inside a table row takes down the whole page — so
 * one malformed timestamp would hide every good row beside it.
 */
export function fmtWhen(iso: string | null | undefined, style: WhenStyle = 'second'): string {
  if (!iso) return '—';
  const at = new Date(iso);
  return Number.isNaN(at.valueOf()) ? '—' : FORMATTERS[style].format(at);
}


export interface ObsQuery<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  /** Re-run the same request — what the header's refresh button calls. */
  reload: () => void;
}

/**
 * Run one observability request and track its three states.
 *
 * Every page in this section fetches exactly once and renders loading, error or
 * data, so the state lives here rather than six times over.
 *
 * `run` must be a `useCallback` — it is the dependency. The in-flight guard is
 * the reason this is a hook and not a copied block: switching the range twice
 * quickly leaves two requests racing, and without the guard the slower one
 * wins and the page shows a window the user already moved off.
 */
export function useObsQuery<T>(run: () => Promise<T>, whenItFails: string): ObsQuery<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);

  const reload = useCallback(() => setAttempt((n) => n + 1), []);

  useEffect(() => {
    let current = true;
    setLoading(true);
    setError(null);

    void (async () => {
      try {
        const next = await run();
        if (current) setData(next);
      } catch (e) {
        if (!current) return;
        setError(e instanceof Error ? e.message : whenItFails);
        setData(null);
      } finally {
        if (current) setLoading(false);
      }
    })();

    return () => { current = false; };
  }, [run, whenItFails, attempt]);

  return { data, loading, error, reload };
}
